import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerModule,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import {
  MatFormField,
  MatFormFieldModule,
  MatHint,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import {
  SiaTooltipDirective,
  SiaTranslatePipe,
  SiaWindowSize,
  jsonDeepClone,
} from '@sia/shared';
import {
  format,
  getHours,
  getMinutes,
  getYear,
  isSameDay,
  setHours,
  setMinutes,
} from 'date-fns';
import { it } from 'date-fns/locale/it';
import { Guid } from 'guid-typescript';
import { Subscription } from 'rxjs';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';
import { SiaErrorHintComponent } from '../sia-error-hint/sia-error-hint.component';
import { SiaTextBoxComponent } from '../sia-text-box/sia-text-box.component';

export const DATE_TIME_FORMAT = {
  parse: {
    dateInput: 'l, LTS',
  },
  display: {
    dateInput: 'MM yyyy',
    monthYearLabel: 'MM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'sia-datetime-box',
  templateUrl: './sia-datetime-box.component.html',
  styleUrls: ['./sia-datetime-box.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatInputModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    FormsModule,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatHint,
    SiaErrorHintComponent,
    SiaTextBoxComponent,
    DatePipe,
    SiaTranslatePipe,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatButtonModule,
    SiaTooltipDirective,
  ],
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: it,
    },
    provideDateFnsAdapter({
      parse: {
        dateInput: 'dd/MM/yyyy',
      },
      display: {
        dateInput: 'dd/MM/yyyy',
        monthYearLabel: 'MM yyyy',
        dateA11yLabel: 'dd/MM/yyyy HH:mm',
        monthYearA11yLabel: 'MMMM yyyy',
      },
    }),
  ],
})
export class SiaDateTimeBoxComponent extends SiaBaseEditorSignalComponent {
  override cdr = inject(ChangeDetectorRef);

  // Inputs as signals
  dateMin = input<any>();
  dateMax = input<any>();
  format = input<string>();
  minutesStep = input<number>();

  // Use viewChild for datepicker access if needed
  datepicker = viewChild(MatDatepicker);

  hoursGuid = Guid.create().toString();
  minutesGuid = Guid.create().toString();

  // Internal state
  stringValue = signal<string>('');

  // Mobile detection
  isMobile = signal(false);

  #dateValueChangeTimeout: any;
  #originalData: any;
  #subs: Subscription[] = [];

  // Flag to ignore next value sync from effect (used when typing shorthand)
  #ignoreNextValueSync = false;

  constructor() {
    super();

    // Effect to sync value change (external) to formatted string
    effect(
      () => {
        const v = this.value();
        // Normalization logic inside effect?
        // We need to avoid infinite loops.
        // If v is string, convert to date and set back?
        untracked(() => {
          if (this.#ignoreNextValueSync) {
            this.#ignoreNextValueSync = false;
            return;
          }
          this.handleValueChange(v);
        });
      },
      { allowSignalWrites: true },
    );

    // Effect to update formatting when format input changes
    effect(() => {
      const fmt = this.format();
      const v = untracked(this.value);
      if (v) {
        this.#formatStringValue(v);
      }
    });
  }

  handleValueChange(v: any, doFormat: boolean = true): boolean {
    // Logic from updateValue, adapted to signals
    let newValue = v;
    let currentValue = untracked(this.value); // current signal value (might be same as v if strictly triggered)

    if (newValue && typeof newValue == 'string') {
      newValue = new Date(newValue);
    }
    if (currentValue && typeof currentValue == 'string') {
      currentValue = new Date(currentValue);
    }

    if (
      currentValue instanceof Date &&
      !isNaN(currentValue.getTime()) &&
      newValue instanceof Date &&
      !isNaN(newValue.getTime()) &&
      !isSameDay(currentValue, newValue)
    ) {
      newValue = setHours(newValue, getHours(currentValue));
      newValue = setMinutes(newValue, getMinutes(currentValue));
    }

    // Deep clone for originalData tracking
    this.#originalData = jsonDeepClone(newValue);

    let changed = false;

    if (!newValue) {
      if (currentValue !== null) {
        this.value.set(null);
        changed = true;
      }
      if (doFormat) this.stringValue.set('');
    } else {
      if (newValue.toString() == 'Invalid Date') {
        if (currentValue !== null) {
          this.value.set(null);
          changed = true;
        }
        if (doFormat) this.stringValue.set('');
      } else {
        // If normalized value differs, update signal
        // Note: using JSON.stringify for date comparison usually works if toJSON is standard
        if (
          currentValue === null ||
          currentValue === undefined ||
          newValue.getTime() !== currentValue.getTime()
        ) {
          this.value.set(newValue);
          changed = true;
        }
        if (doFormat) this.#formatStringValue(newValue);
      }
    }

    this.#notifyValueChanged(newValue);
    return changed;
  }

  // Called from template (ngModelChange of datepicker or manual input processing)
  updateValue(v: any, updateStringValue: boolean = true) {
    // Manual update flow
    if (!updateStringValue) {
      this.#ignoreNextValueSync = true;
    } else {
      this.#ignoreNextValueSync = false;
    }

    const changed = this.handleValueChange(v, updateStringValue);

    // If value didn't change, effect won't trigger, so cleanup flag
    if (!changed && this.#ignoreNextValueSync) {
      this.#ignoreNextValueSync = false;
    }
  }

  // Re-implementing formatting logic separate from value update
  #formatStringValue(v: any) {
    if (!v) {
      this.stringValue.set('');
      return;
    }
    var timestamp = Date.parse(v);
    if (isNaN(timestamp)) {
      return;
    }
    this.stringValue.set(format(v, this.format() ?? 'dd/MM/yyyy'));
  }

  #isValidDayMonth(day: number, month: number, year?: number): boolean {
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (year && month === 2) {
      const isLeapYear =
        (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
      if (isLeapYear && day > 29) return false;
      if (!isLeapYear && day > 28) return false;
    } else if (day > daysInMonth[month - 1]) {
      return false;
    }
    return true;
  }

  formatInputString(updateStringValue: boolean = true) {
    let hours = 0;
    let minutes = 0;
    const val = this.value();
    if (val) {
      hours = getHours(val);
      minutes = getMinutes(val);
    }

    const strVal = this.stringValue();

    if (strVal == 'today' || strVal == 'oggi' || strVal == 'o') {
      this.updateValue(new Date(), updateStringValue);
      // Effect will handle formatting
    } else if (/^\d{6}$/.test(strVal)) {
      const day = parseInt(strVal.substring(0, 2));
      const month = parseInt(strVal.substring(2, 4));
      const year = 2000 + parseInt(strVal.substring(4, 6));
      if (this.#isValidDayMonth(day, month, year)) {
        this.updateValue(
          new Date(year, month - 1, day, hours, minutes),
          updateStringValue,
        );
      } else {
        this.#setDateToNull(updateStringValue);
      }
    } else if (/^\d{8}$/.test(strVal)) {
      const day = parseInt(strVal.substring(0, 2));
      const month = parseInt(strVal.substring(2, 4));
      const year = parseInt(strVal.substring(4, 8));
      if (this.#isValidDayMonth(day, month, year)) {
        this.updateValue(
          new Date(year, month - 1, day, hours, minutes),
          updateStringValue,
        );
      } else {
        this.#setDateToNull(updateStringValue);
      }
    } else if (/^\d{2}\/\d{2}\/\d{2}$/.test(strVal)) {
      let [dayStr, monthStr, yearStr] = strVal.split('/');
      const day = parseInt(dayStr);
      const month = parseInt(monthStr);
      const year = 2000 + parseInt(yearStr);
      if (this.#isValidDayMonth(day, month, year)) {
        this.updateValue(
          new Date(year, month - 1, day, hours, minutes),
          updateStringValue,
        );
      } else {
        this.#setDateToNull(updateStringValue);
      }
    } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(strVal)) {
      let [dayStr, monthStr, yearStr] = strVal.split('/');
      const day = parseInt(dayStr);
      const month = parseInt(monthStr);
      const year = parseInt(yearStr);
      if (this.#isValidDayMonth(day, month, year)) {
        this.updateValue(
          new Date(year, month - 1, day, hours, minutes),
          updateStringValue,
        );
      } else {
        this.#setDateToNull(updateStringValue);
      }
    } else if (/^\d{2}\/\d{2}$/.test(strVal)) {
      let [dayStr, monthStr] = strVal.split('/');
      const day = parseInt(dayStr);
      const month = parseInt(monthStr);
      const year = getYear(new Date());
      if (this.#isValidDayMonth(day, month, year)) {
        this.updateValue(
          new Date(year, month - 1, day, hours, minutes),
          updateStringValue,
        );
      } else {
        this.#setDateToNull(updateStringValue);
      }
    } else if (/^\d{4}$/.test(strVal)) {
      const day = parseInt(strVal.substring(0, 2));
      const month = parseInt(strVal.substring(2, 4));
      const year = getYear(new Date());
      if (this.#isValidDayMonth(day, month, year)) {
        this.updateValue(
          new Date(year, month - 1, day, hours, minutes),
          updateStringValue,
        );
      } else {
        this.#setDateToNull(updateStringValue);
      }
    } else {
      // If empty or invalid regex, nullify?
      if (!strVal) {
        this.#setDateToNull(updateStringValue);
      } else {
        // If typed something invalid that date-fns/datepicker can't parse?
        // Keep as is in string, but invalid in value?
        this.#setDateToNull(updateStringValue);
      }
    }

    // Formatting timeout to look nice after typing
    // We already set value, which triggers effect -> formatting.
    // If updateStringValue is false (e.g. keyup), we might want to avoid effect overwriting?
    // But setting value triggers effect.
    // We can use untracked in effect? No.
  }

  #setDateToNull(updateStringValue: boolean = true) {
    this.updateValue(null, updateStringValue);
    if (updateStringValue) {
      this.stringValue.set('');
    }
  }

  #notifyValueChanged(v: any) {
    clearTimeout(this.#dateValueChangeTimeout);
    this.#dateValueChangeTimeout = setTimeout(() => {
      this.valueChanged.emit(v);
    }, 300);
  }

  setAsToday() {
    this.value.set(new Date());
  }

  ngOnInit() {
    let s0 = this.siaSizeDetector.onResize$.subscribe(
      (size: SiaWindowSize | null) => {
        if (size) {
          this.isMobile.set(size.isMobile ?? false);
        }
      },
    );
    this.#subs.push(s0);
  }

  override ngOnDestroy() {
    clearTimeout(this.#dateValueChangeTimeout);
    this.#subs.forEach((s) => s.unsubscribe());
    // this.datepicker()?.ngOnDestroy(); // Accessed via signal ViewChild
    // Logic for destroying datepicker ref handled by Angular mostly
    super.ngOnDestroy();
  }

  // Method to bind to stringValue change from template
  onStringValueChange(val: string) {
    this.stringValue.set(val);
  }
}
